import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"
import { addDays, format, parseISO } from "date-fns"
import {
  CheckCircle2,
  Circle,
  Clock,
  Loader2,
  MapPin,
  Search,
  Truck,
} from "lucide-react"
import { useFeatureFlagEnabled } from "posthog-js/react"
import type { TFunction } from "i18next"
import { useTranslation } from "react-i18next"
import AdHocCommands from "@/api/adHocCommands"
import { Input } from "@/components/ui/input"
import { matchesPlateFilter } from "@/components/timeline/matchesPlateFilter"
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { type GboxTimelineFeed } from "@/lib/gboxTimeline"
import GboxTimelineParser from "@/lib/gboxTimelineParser"
import {
  selectAllDriverRouteAndLocationDetailsMap,
  selectDriversJids,
} from "@/reducers/accountChatsSelectors"
import NavigatorTimelineParser from "@/lib/navigatorTimelineParser"
import PlatformScienceTimelineParser from "@/lib/platformScienceTimelineParser"
import { useAppSelector } from "@/reducers/hooks"
import { selectChats } from "@/reducers/chatsSliceSelectors"
import {
  buildTimelineData,
  TimelineChain,
  TimelineChainEvent,
  TimelineData,
  TimelineSegment,
  TimelineTone,
} from "./buildTimelineData"
import mockTimelineDataJson from "./data/mockTimeline.json"

type TimelineViewProps = {
  chatContent: ReactNode
  workflowContent: ReactNode
}

const segmentToneClasses: Record<TimelineTone, string> = {
  danger:
    "border-rose-200 bg-rose-100 text-rose-950 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-100",
  info: "border-sky-200 bg-sky-100 text-sky-950 dark:border-sky-900 dark:bg-sky-950 dark:text-sky-100",
  neutral:
    "border-slate-200 bg-slate-100 text-slate-900 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100",
  success:
    "border-emerald-200 bg-emerald-100 text-emerald-950 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-100",
  warning:
    "border-amber-200 bg-amber-100 text-amber-950 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100",
}

const chainBarToneClasses: Record<TimelineTone, string> = {
  danger:
    "border-rose-200/70 bg-rose-50 hover:bg-rose-100/70 dark:border-rose-900/70 dark:bg-rose-950/50 dark:hover:bg-rose-900/40",
  info: "border-sky-200/70 bg-sky-50 hover:bg-sky-100/70 dark:border-sky-900/70 dark:bg-sky-950/50 dark:hover:bg-sky-900/40",
  neutral:
    "border-slate-200 bg-white hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:hover:bg-slate-800",
  success:
    "border-emerald-200/70 bg-emerald-50 hover:bg-emerald-100/70 dark:border-emerald-900/70 dark:bg-emerald-950/50 dark:hover:bg-emerald-900/40",
  warning:
    "border-amber-200/70 bg-amber-50 hover:bg-amber-100/70 dark:border-amber-900/70 dark:bg-amber-950/50 dark:hover:bg-amber-900/40",
}

const chainAccentToneClasses: Record<TimelineTone, string> = {
  danger: "bg-rose-500",
  info: "bg-sky-500",
  neutral: "bg-slate-300 dark:bg-slate-600",
  success: "bg-emerald-500",
  warning: "bg-amber-500",
}

const chainBadgeToneClasses: Record<TimelineTone, string> = {
  danger: "bg-rose-500 text-white",
  info: "bg-sky-500 text-white",
  neutral: "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200",
  success: "bg-emerald-500 text-white",
  warning: "bg-amber-500 text-white",
}

const eventStatusBadgeClasses: Record<TimelineTone, string> = {
  danger:
    "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-300",
  info: "border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-900 dark:bg-sky-950 dark:text-sky-300",
  neutral:
    "border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300",
  success:
    "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300",
  warning:
    "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300",
}

const TIMELINE_DRIVER_COLUMN_WIDTH = 190
const TIMELINE_DAY_COLUMN_WIDTH = 220
const TIMELINE_CHAIN_BAR_HEIGHT = 44
const TIMELINE_CHAIN_LANE_SPACING = 52
const TIMELINE_ROW_MIN_HEIGHT = 96

const countryCodeToFlagEmoji = (countryCode: string) =>
  /^[A-Z]{2}$/.test(countryCode)
    ? countryCode
        .toUpperCase()
        .replace(/./g, (char) =>
          String.fromCodePoint(127397 + char.charCodeAt(0)),
        )
    : "•"

const buildEmptyTimelineData = (
  t: TFunction,
  subtitle: string,
): TimelineData => ({
  title: t("planner.liveTimeline.title"),
  subtitle,
  dateOffsets: [-1, 0, 1, 2, 3, 4, 5],
  summary: [
    { label: t("planner.liveTimeline.summary.drivers"), value: "0" },
    { label: t("planner.liveTimeline.summary.alerts"), value: "0" },
    { label: t("planner.liveTimeline.summary.visibleEvents"), value: "0" },
  ],
  rows: [],
})

// The load error is kept as a key, so it is worded at render time.
type TimelineErrorKey =
  | "planner.liveTimeline.clientUnavailable"
  | "planner.liveTimeline.loadFailed"

const TONE_LABEL_KEYS = {
  danger: "planner.liveTimeline.tones.danger",
  info: "planner.liveTimeline.tones.info",
  neutral: "planner.liveTimeline.tones.neutral",
  success: "planner.liveTimeline.tones.success",
  warning: "planner.liveTimeline.tones.warning",
} as const

type SelectedTimelineChain = {
  chain: TimelineChain
  carCode: string
  driverName: string
}

type SelectedTimelineSegment = {
  carCode: string
  driverName: string
  segment: TimelineSegment
}

type SelectedTimelineItem =
  | { type: "chain"; value: SelectedTimelineChain }
  | { type: "segment"; value: SelectedTimelineSegment }

const mockTimelineData = mockTimelineDataJson as TimelineData

const formatTimelineTimestamp = (value?: string) => {
  if (!value) return "—"
  const parsed = parseISO(value)
  if (Number.isNaN(parsed.getTime())) return value
  return format(parsed, "dd.MM.yyyy HH:mm")
}

const formatDelay = (seconds?: number) => {
  if (typeof seconds !== "number") return null
  const sign = seconds >= 0 ? "+" : "-"
  const absoluteSeconds = Math.abs(seconds)
  const hours = Math.floor(absoluteSeconds / 3600)
  const minutes = Math.floor((absoluteSeconds % 3600) / 60)
  if (hours > 0) {
    return `${sign}${hours}h ${minutes}m`
  }
  return `${sign}${minutes}m`
}

const getEventDelaySeconds = (event: TimelineChainEvent): number | undefined =>
  event.delayWithTachoSeconds ?? event.delaySeconds

const getChainBadge = (
  t: TFunction,
  chain: TimelineChain,
): { label: string; tone: TimelineTone } | null => {
  const delays = chain.events
    .map((event) => getEventDelaySeconds(event))
    .filter((value): value is number => typeof value === "number")
  const worstDelay = delays.length > 0 ? Math.max(...delays) : undefined
  const hasLateStatus = chain.events.some(
    (event) => event.timelinessStatus === "late",
  )
  const hasAheadStatus = chain.events.some(
    (event) => event.timelinessStatus === "ahead",
  )
  const isActive = chain.events.some(
    (event) => !event.isCompleted || event.isCurrentEvent,
  )

  if (typeof worstDelay === "number" && worstDelay >= 300) {
    return {
      label: formatDelay(worstDelay) || t("planner.liveTimeline.status.late"),
      tone: "danger",
    }
  }
  if (hasLateStatus) {
    return { label: t("planner.liveTimeline.status.late"), tone: "danger" }
  }

  if (typeof worstDelay === "number" && worstDelay <= -300) {
    return {
      label: formatDelay(worstDelay) || t("planner.liveTimeline.status.ahead"),
      tone: "success",
    }
  }
  if (hasAheadStatus) {
    return { label: t("planner.liveTimeline.status.ahead"), tone: "success" }
  }

  if (isActive) {
    return { label: t("planner.liveTimeline.status.onTime"), tone: "success" }
  }

  if (chain.events.every((event) => event.isCompleted)) {
    return { label: t("planner.liveTimeline.status.done"), tone: "neutral" }
  }

  return null
}

const getEventStatus = (
  t: TFunction,
  event: TimelineChainEvent,
): { label: string; tone: TimelineTone } => {
  const delay = getEventDelaySeconds(event)
  if (typeof delay === "number" && delay >= 300) {
    const label = formatDelay(delay)
    return {
      label: label
        ? t("planner.liveTimeline.status.lateBy", { delay: label })
        : t("planner.liveTimeline.status.late"),
      tone: "danger",
    }
  }
  if (event.timelinessStatus === "late") {
    return { label: t("planner.liveTimeline.status.late"), tone: "danger" }
  }
  if (typeof delay === "number" && delay <= -300) {
    const label = formatDelay(delay)
    return {
      label: label
        ? t("planner.liveTimeline.status.aheadBy", { delay: label })
        : t("planner.liveTimeline.status.ahead"),
      tone: "success",
    }
  }
  if (event.timelinessStatus === "ahead") {
    return { label: t("planner.liveTimeline.status.ahead"), tone: "success" }
  }
  if (event.isCurrentEvent) {
    return { label: t("planner.liveTimeline.status.onTime"), tone: "success" }
  }
  if (event.isCompleted) {
    return {
      label: t("planner.liveTimeline.status.completed"),
      tone: "neutral",
    }
  }
  return { label: t("planner.liveTimeline.status.onTime"), tone: "success" }
}

const TimelineView = ({ chatContent, workflowContent }: TimelineViewProps) => {
  const { t } = useTranslation()
  const driversByJid = useAppSelector(selectAllDriverRouteAndLocationDetailsMap)
  const bookmarkedDriversByJid = useAppSelector(selectDriversJids)
  const drivers = Object.values(driversByJid)
  const bookmarkedDriverChatJids = useMemo(
    () => Array.from(bookmarkedDriversByJid.values()).sort(),
    [bookmarkedDriversByJid],
  )
  const chats = useAppSelector(selectChats)
  const isGboxTimelineViewEnabled = Boolean(
    useFeatureFlagEnabled("gbox-timeline-view"),
  )
  const shouldShowSidePanel = false
  const isNavigatorTimelineViewEnabled = Boolean(
    useFeatureFlagEnabled("navigator-timeline-view"),
  )
  const isPlatformScienceTimelineViewEnabled = Boolean(
    useFeatureFlagEnabled("platform-science-timeline-view"),
  )
  const isLiveTimelineViewEnabled =
    isGboxTimelineViewEnabled ||
    isNavigatorTimelineViewEnabled ||
    isPlatformScienceTimelineViewEnabled
  const [timelineFeeds, setTimelineFeeds] = useState<GboxTimelineFeed[]>([])
  const [timelineLoading, setTimelineLoading] = useState(true)
  const [timelineError, setTimelineError] = useState<TimelineErrorKey | null>(
    null,
  )
  const [selectedItem, setSelectedItem] = useState<SelectedTimelineItem | null>(
    null,
  )
  useEffect(() => {
    let isCancelled = false

    const loadTimeline = async () => {
      if (!isLiveTimelineViewEnabled) {
        setTimelineFeeds([])
        setTimelineError(null)
        setTimelineLoading(false)
        return
      }

      if (!window.XMPP) {
        setTimelineFeeds([])
        setTimelineError("planner.liveTimeline.clientUnavailable")
        setTimelineLoading(false)
        return
      }

      if (bookmarkedDriverChatJids.length === 0) {
        setTimelineFeeds([])
        setTimelineError(null)
        setTimelineLoading(false)
        return
      }

      setTimelineLoading(true)
      setTimelineError(null)
      try {
        const feedRequests: Promise<GboxTimelineFeed[]>[] = []
        if (isGboxTimelineViewEnabled) {
          feedRequests.push(
            AdHocCommands.getGboxTimelineView(
              window.XMPP!,
              bookmarkedDriverChatJids,
            ).then(GboxTimelineParser.parse),
          )
        }
        if (isNavigatorTimelineViewEnabled) {
          feedRequests.push(
            AdHocCommands.getNavigatorTimelineView(window.XMPP!).then(
              NavigatorTimelineParser.parse,
            ),
          )
        }
        if (isPlatformScienceTimelineViewEnabled) {
          feedRequests.push(
            AdHocCommands.getPlatformScienceTimelineView(
              window.XMPP!,
              bookmarkedDriverChatJids,
            ).then(PlatformScienceTimelineParser.parse),
          )
        }

        const results = await Promise.allSettled(feedRequests)
        if (isCancelled) return
        const feeds = results.flatMap((result) =>
          result.status === "fulfilled" ? result.value : [],
        )
        const failedResults = results.filter(
          (result) => result.status === "rejected",
        )
        if (failedResults.length > 0) {
          console.warn("Failed to load some timeline providers", failedResults)
        }
        setTimelineFeeds(feeds)
        setTimelineError(
          failedResults.length > 0 && feeds.length === 0
            ? "planner.liveTimeline.loadFailed"
            : null,
        )
      } catch (error) {
        console.warn("Failed to load timeline view", error)
        if (isCancelled) return
        setTimelineFeeds([])
        setTimelineError("planner.liveTimeline.loadFailed")
      } finally {
        if (!isCancelled) {
          setTimelineLoading(false)
        }
      }
    }

    void loadTimeline()
    return () => {
      isCancelled = true
    }
  }, [
    isGboxTimelineViewEnabled,
    isLiveTimelineViewEnabled,
    isNavigatorTimelineViewEnabled,
    isPlatformScienceTimelineViewEnabled,
    bookmarkedDriverChatJids,
  ])

  const liveTimelineData = buildTimelineData(drivers, timelineFeeds, chats)
  const timelineData = isLiveTimelineViewEnabled
    ? (liveTimelineData ??
      buildEmptyTimelineData(
        t,
        timelineLoading
          ? t("planner.liveTimeline.loadingSubtitle")
          : timelineError
            ? t(timelineError)
            : t("planner.liveTimeline.noData"),
      ))
    : mockTimelineData

  const headerTimelineScrollRef = useRef<HTMLDivElement>(null)
  const bodyRowsScrollRef = useRef<HTMLDivElement>(null)
  const timelineViewportRef = useRef<HTMLDivElement>(null)
  // Once the user drags the timeline we stop auto-centering on the current-time
  // line, so we don't fight their manual scrolling.
  const userScrolledTimelineRef = useRef(false)
  const dragStateRef = useRef<{
    pointerId: number
    startScrollLeft: number
    startScrollTop: number
    startX: number
    startY: number
  } | null>(null)
  const [isDraggingTimeline, setIsDraggingTimeline] = useState(false)
  const [timelineScrollLeft, setTimelineScrollLeft] = useState(0)
  const [timelineViewportWidth, setTimelineViewportWidth] = useState(0)
  const [plateFilter, setPlateFilter] = useState("")
  const currentDay = new Date()
  const dates = timelineData.dateOffsets.map((offset) =>
    format(addDays(currentDay, offset), "dd.MM.yyyy"),
  )
  const currentDateIndex = Math.max(timelineData.dateOffsets.indexOf(0), 0)
  const totalDates = dates.length
  const timelineWidth = totalDates * TIMELINE_DAY_COLUMN_WIDTH
  const contentGridTemplateColumns = `${TIMELINE_DRIVER_COLUMN_WIDTH}px minmax(0, 1fr)`
  const datesGridTemplateColumns = `repeat(${totalDates}, ${TIMELINE_DAY_COLUMN_WIDTH}px)`
  const currentLineOffset = `${(currentDateIndex + 0.5) * TIMELINE_DAY_COLUMN_WIDTH}px`
  const currentLineOffsetPx =
    (currentDateIndex + 0.5) * TIMELINE_DAY_COLUMN_WIDTH
  const startOffset = timelineData.dateOffsets[0] ?? 0
  const visibleRows = timelineData.rows.filter((row) =>
    matchesPlateFilter(row.dispatcherCode, plateFilter, row.roomName),
  )

  const getMaxTimelineScrollLeft = useCallback(() => {
    const viewport =
      timelineViewportRef.current ?? headerTimelineScrollRef.current
    if (!viewport) return 0

    return Math.max(timelineWidth - viewport.clientWidth, 0)
  }, [timelineWidth])

  const syncTimelineScrollLeft = useCallback(
    (nextScrollLeft: number) => {
      const clampedScrollLeft = Math.max(
        0,
        Math.min(nextScrollLeft, getMaxTimelineScrollLeft()),
      )

      const headerTimelineScroll = headerTimelineScrollRef.current
      if (
        headerTimelineScroll &&
        Math.abs(headerTimelineScroll.scrollLeft - clampedScrollLeft) > 1
      ) {
        headerTimelineScroll.scrollLeft = clampedScrollLeft
      }

      setTimelineScrollLeft(clampedScrollLeft)
    },
    [getMaxTimelineScrollLeft],
  )

  // Track the real (laid-out) width of the timeline viewport. A ResizeObserver
  // reports the final width even when the resizable panel settles a frame or
  // two after mount, so centering no longer has to guess when layout is ready.
  useEffect(() => {
    const viewport =
      timelineViewportRef.current ?? headerTimelineScrollRef.current
    if (!viewport) return

    const updateWidth = () => setTimelineViewportWidth(viewport.clientWidth)
    updateWidth()

    const observer = new ResizeObserver(updateWidth)
    observer.observe(viewport)
    return () => observer.disconnect()
    // Re-attach when the first data row mounts (the observed ref target changes
    // from the header fallback to the first row's timeline cell).
  }, [timelineData.rows.length])

  // Center the viewport on the current-time line once data and a real viewport
  // width are available. Re-runs if the width changes (panel resize/restore)
  // until the user drags the timeline themselves.
  useEffect(() => {
    if (userScrolledTimelineRef.current) return
    if (timelineData.rows.length === 0) return
    if (timelineViewportWidth <= 0) return

    const centeredScrollLeft = Math.max(
      0,
      Math.min(
        currentLineOffsetPx - timelineViewportWidth / 2,
        getMaxTimelineScrollLeft(),
      ),
    )

    syncTimelineScrollLeft(centeredScrollLeft)
  }, [
    currentLineOffsetPx,
    getMaxTimelineScrollLeft,
    syncTimelineScrollLeft,
    timelineData.rows.length,
    timelineViewportWidth,
  ])

  const handleHeaderTimelineScroll = (
    event: React.UIEvent<HTMLDivElement, UIEvent>,
  ) => {
    setTimelineScrollLeft(event.currentTarget.scrollLeft)
  }

  const handleTimelinePointerDown = (
    event: React.PointerEvent<HTMLDivElement>,
  ) => {
    if (event.button !== 0) return

    const bodyRowsScroll = bodyRowsScrollRef.current
    if (!bodyRowsScroll) return

    userScrolledTimelineRef.current = true

    dragStateRef.current = {
      pointerId: event.pointerId,
      startScrollLeft: timelineScrollLeft,
      startScrollTop: bodyRowsScroll.scrollTop,
      startX: event.clientX,
      startY: event.clientY,
    }

    setIsDraggingTimeline(true)
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const handleTimelinePointerMove = (
    event: React.PointerEvent<HTMLDivElement>,
  ) => {
    const bodyRowsScroll = bodyRowsScrollRef.current
    const dragState = dragStateRef.current

    if (
      !bodyRowsScroll ||
      !dragState ||
      dragState.pointerId !== event.pointerId
    ) {
      return
    }

    const deltaX = event.clientX - dragState.startX
    const deltaY = event.clientY - dragState.startY

    syncTimelineScrollLeft(dragState.startScrollLeft - deltaX)
    bodyRowsScroll.scrollTop = dragState.startScrollTop - deltaY
  }

  const stopTimelineDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    const dragState = dragStateRef.current

    if (!dragState || dragState.pointerId !== event.pointerId) return

    dragStateRef.current = null
    setIsDraggingTimeline(false)

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
  }

  const getOffsetPixels = useCallback(
    (offset: number) => (offset - startOffset) * TIMELINE_DAY_COLUMN_WIDTH,
    [startOffset],
  )

  return (
    <ResizablePanelGroup
      key={shouldShowSidePanel ? "timeline-with-side-panel" : "timeline-full"}
      autoSaveId={
        shouldShowSidePanel
          ? "slickshift-timeline-layout"
          : "slickshift-timeline-full-layout"
      }
      direction="horizontal"
      className="h-full"
    >
      <ResizablePanel defaultSize={shouldShowSidePanel ? 70 : 100} minSize={45}>
        <div className="flex h-full min-h-0 flex-col bg-[var(--background-primary)]">
          <div className="border-b bg-[var(--panel-color)] px-6 py-5">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="space-y-1">
                <h1 className="text-2xl font-semibold text-foreground">
                  {timelineData.title}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {timelineData.subtitle}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative w-56">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <Input
                    type="search"
                    placeholder={t("planner.liveTimeline.filterPlaceholder")}
                    aria-label={t("planner.liveTimeline.filterLabel")}
                    value={plateFilter}
                    onChange={(event) => setPlateFilter(event.target.value)}
                    className="h-9 rounded-md bg-white pl-10 dark:bg-input/30"
                  />
                </div>
                {timelineData.summary.map((item) => (
                  <div
                    key={item.label}
                    className="rounded-full border bg-background px-3 py-1.5 text-sm shadow-sm"
                  >
                    <span className="font-semibold text-foreground">
                      {item.value}
                    </span>
                    <span className="ml-2 text-muted-foreground">
                      {item.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-hidden px-6 py-4">
            <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border bg-[var(--panel-color)] shadow-sm">
              <div className="border-b bg-[var(--panel-color)]">
                <div
                  className="grid"
                  style={{ gridTemplateColumns: contentGridTemplateColumns }}
                >
                  <div className="border-r bg-[var(--panel-color)] px-4 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                    {t("planner.liveTimeline.carColumn")}
                  </div>
                  <div
                    ref={headerTimelineScrollRef}
                    className="overflow-x-auto overflow-y-hidden scrollbar-borderless"
                    onScroll={handleHeaderTimelineScroll}
                  >
                    <div
                      className="relative"
                      style={{ width: `${timelineWidth}px` }}
                    >
                      <div
                        className="grid"
                        style={{
                          gridTemplateColumns: datesGridTemplateColumns,
                        }}
                      >
                        {dates.map((date) => (
                          <div
                            key={date}
                            className="border-l px-4 py-3 text-xs font-semibold text-muted-foreground first:border-l-0"
                          >
                            {date}
                          </div>
                        ))}
                      </div>
                      <div
                        className="pointer-events-none absolute bottom-0 top-0 w-px bg-sky-400/80"
                        style={{ left: currentLineOffset }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div
                ref={bodyRowsScrollRef}
                className="relative flex-1 overflow-y-auto"
              >
                {isLiveTimelineViewEnabled &&
                  timelineLoading &&
                  timelineData.rows.length === 0 && (
                    <div className="flex h-full items-center justify-center py-16 text-muted-foreground">
                      <div className="flex flex-col items-center gap-3">
                        <Loader2 className="h-6 w-6 animate-spin text-sky-500" />
                        <p className="text-sm">
                          {t("planner.liveTimeline.loading")}
                        </p>
                      </div>
                    </div>
                  )}
                {!timelineLoading && timelineData.rows.length === 0 && (
                  <div className="flex h-full items-center justify-center py-16 text-center text-sm text-muted-foreground">
                    <p className="max-w-xs">{timelineData.subtitle}</p>
                  </div>
                )}
                {isLiveTimelineViewEnabled &&
                  timelineLoading &&
                  timelineData.rows.length > 0 && (
                    <div className="pointer-events-none sticky top-0 z-10 flex items-center justify-center gap-2 border-b border-sky-100 bg-sky-50/90 py-1.5 text-[11px] font-medium text-sky-700 backdrop-blur dark:border-sky-900 dark:bg-sky-950/80 dark:text-sky-300">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      {t("planner.liveTimeline.refreshing")}
                    </div>
                  )}
                {timelineData.rows.length > 0 &&
                  visibleRows.length === 0 &&
                  plateFilter.trim() !== "" && (
                    <div className="flex h-full items-center justify-center py-16 text-center text-sm text-muted-foreground">
                      <p className="max-w-xs">
                        {t("planner.liveTimeline.noCarsMatch", {
                          query: plateFilter,
                        })}
                      </p>
                    </div>
                  )}
                <div className="divide-y">
                  {visibleRows.map((row, index) => {
                    const laneCount = Math.max(row.laneCount || 1, 1)
                    const rowMinHeight = Math.max(
                      TIMELINE_ROW_MIN_HEIGHT,
                      32 + laneCount * TIMELINE_CHAIN_LANE_SPACING,
                    )

                    return (
                      <div
                        key={`${row.dispatcherCode}-${row.driverName}`}
                        className="grid"
                        style={{
                          gridTemplateColumns: contentGridTemplateColumns,
                          minHeight: `${rowMinHeight}px`,
                        }}
                      >
                        <div className="flex items-start gap-3 border-r bg-[var(--panel-color)] px-4 py-4">
                          <div className="rounded-xl border bg-background p-2 text-muted-foreground">
                            <Truck className="h-4 w-4" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-foreground">
                              {row.roomName ?? row.dispatcherCode}
                            </p>
                            <p className="truncate text-sm text-blue-600 dark:text-blue-400">
                              {row.driverName}
                            </p>
                          </div>
                        </div>

                        <div
                          ref={index === 0 ? timelineViewportRef : undefined}
                          className={`relative overflow-hidden ${isDraggingTimeline ? "cursor-grabbing select-none" : "cursor-grab"}`}
                          onPointerDown={handleTimelinePointerDown}
                          onPointerMove={handleTimelinePointerMove}
                          onPointerUp={stopTimelineDrag}
                          onPointerCancel={stopTimelineDrag}
                          style={{ touchAction: "none" }}
                        >
                          <div
                            className="relative h-full"
                            style={{
                              transform: `translateX(-${timelineScrollLeft}px)`,
                              width: `${timelineWidth}px`,
                            }}
                          >
                            <div
                              className="grid h-full"
                              style={{
                                gridTemplateColumns: datesGridTemplateColumns,
                              }}
                            >
                              {dates.map((date) => (
                                <div
                                  key={`${row.dispatcherCode}-${date}`}
                                  className="border-l first:border-l-0"
                                />
                              ))}
                            </div>

                            <div
                              className="pointer-events-none absolute bottom-0 top-0 w-px bg-sky-400/80"
                              style={{ left: currentLineOffset }}
                            />

                            <div
                              className="absolute inset-y-4"
                              style={{ width: timelineWidth }}
                            >
                              {row.chains && row.chains.length > 0
                                ? row.chains.map((chain) => {
                                    const laneTop =
                                      16 +
                                      chain.lane * TIMELINE_CHAIN_LANE_SPACING
                                    const left = getOffsetPixels(
                                      chain.startOffset,
                                    )
                                    const width = Math.max(
                                      getOffsetPixels(chain.endOffset) - left,
                                      140,
                                    )
                                    const badge = getChainBadge(t, chain)

                                    return (
                                      <button
                                        type="button"
                                        key={`${row.dispatcherCode}-${chain.key}`}
                                        className="absolute text-left"
                                        onClick={() =>
                                          setSelectedItem({
                                            type: "chain",
                                            value: {
                                              chain,
                                              carCode: row.dispatcherCode,
                                              driverName: row.driverName,
                                            },
                                          })
                                        }
                                        onPointerDown={(event) =>
                                          event.stopPropagation()
                                        }
                                        onPointerMove={(event) =>
                                          event.stopPropagation()
                                        }
                                        onPointerUp={(event) =>
                                          event.stopPropagation()
                                        }
                                        style={{
                                          left: `${left}px`,
                                          top: `${laneTop}px`,
                                          width: `${width}px`,
                                          height: `${TIMELINE_CHAIN_BAR_HEIGHT}px`,
                                        }}
                                      >
                                        <div
                                          className={`group relative flex h-full items-center gap-2 overflow-hidden rounded-lg border pl-3 pr-2 transition-all hover:-translate-y-px hover:shadow-sm ${chainBarToneClasses[chain.tone]}`}
                                        >
                                          <div
                                            className={`absolute inset-y-1.5 left-0 w-1 rounded-r-full ${chainAccentToneClasses[chain.tone]}`}
                                          />

                                          <div className="min-w-0 flex-1">
                                            <p className="truncate text-[13px] font-semibold leading-tight text-foreground">
                                              {chain.label}
                                            </p>
                                            <p className="mt-0.5 flex items-center gap-1 truncate text-[11px] leading-tight text-muted-foreground">
                                              <span aria-hidden>
                                                {countryCodeToFlagEmoji(
                                                  chain.startCode,
                                                )}
                                              </span>
                                              <span
                                                className="text-muted-foreground/70"
                                                aria-hidden
                                              >
                                                →
                                              </span>
                                              <span aria-hidden>
                                                {countryCodeToFlagEmoji(
                                                  chain.endCode,
                                                )}
                                              </span>
                                            </p>
                                          </div>

                                          {badge && (
                                            <span
                                              className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${chainBadgeToneClasses[badge.tone]}`}
                                            >
                                              {badge.label}
                                            </span>
                                          )}
                                        </div>
                                      </button>
                                    )
                                  })
                                : row.segments.map((segment, segmentIndex) => (
                                    <button
                                      type="button"
                                      key={`${segment.label}-${segmentIndex}`}
                                      className={`absolute flex h-9 items-center gap-2 overflow-hidden rounded-xl border px-3 text-sm font-medium shadow-sm ${segmentToneClasses[segment.tone]}`}
                                      onClick={() =>
                                        setSelectedItem({
                                          type: "segment",
                                          value: {
                                            carCode: row.dispatcherCode,
                                            driverName: row.driverName,
                                            segment,
                                          },
                                        })
                                      }
                                      onPointerDown={(event) =>
                                        event.stopPropagation()
                                      }
                                      onPointerMove={(event) =>
                                        event.stopPropagation()
                                      }
                                      onPointerUp={(event) =>
                                        event.stopPropagation()
                                      }
                                      style={{
                                        left: `${segment.startColumn * TIMELINE_DAY_COLUMN_WIDTH}px`,
                                        top: `${24 + segmentIndex * 22}px`,
                                        width: `${segment.span * TIMELINE_DAY_COLUMN_WIDTH}px`,
                                      }}
                                    >
                                      <span className="truncate">
                                        {segment.label}
                                      </span>
                                    </button>
                                  ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </ResizablePanel>

      {shouldShowSidePanel && (
        <>
          <ResizableHandle withHandle />

          <ResizablePanel defaultSize={30} minSize={24}>
            <div className="h-full min-h-0 border-l">
              <ResizablePanelGroup
                autoSaveId="slickshift-timeline-side-layout"
                direction="vertical"
                className="h-full"
              >
                <ResizablePanel defaultSize={20} minSize={15}>
                  <div className="h-full min-h-0 bg-[var(--panel-color)]">
                    {workflowContent}
                  </div>
                </ResizablePanel>

                <ResizableHandle withHandle />

                <ResizablePanel defaultSize={80} minSize={35}>
                  <div className="h-full min-h-0 bg-[var(--panel-color)]">
                    {chatContent}
                  </div>
                </ResizablePanel>
              </ResizablePanelGroup>
            </div>
          </ResizablePanel>
        </>
      )}

      <Dialog
        open={Boolean(selectedItem)}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedItem(null)
          }
        }}
      >
        <DialogContent className="max-h-[85vh] w-[calc(100vw-2rem)] max-w-2xl overflow-hidden overflow-y-auto">
          {selectedItem?.type === "chain" && (
            <>
              <DialogHeader className="min-w-0 pr-8">
                <DialogTitle className="flex min-w-0 items-center gap-2 text-lg">
                  <span aria-hidden className="shrink-0">
                    {countryCodeToFlagEmoji(selectedItem.value.chain.startCode)}
                  </span>
                  <span className="min-w-0 flex-1 truncate">
                    {selectedItem.value.chain.label}
                  </span>
                  {selectedItem.value.chain.endCode !==
                    selectedItem.value.chain.startCode && (
                    <>
                      <span className="shrink-0 text-muted-foreground">→</span>
                      <span aria-hidden className="shrink-0">
                        {countryCodeToFlagEmoji(
                          selectedItem.value.chain.endCode,
                        )}
                      </span>
                    </>
                  )}
                </DialogTitle>
                <DialogDescription>
                  {selectedItem.value.carCode +
                    " • " +
                    (selectedItem.value.driverName ||
                      t("planner.liveTimeline.unknownDriver"))}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <ol className="relative space-y-3 border-l border-border pl-6">
                  {selectedItem.value.chain.events.map((event) => {
                    const status = getEventStatus(t, event)
                    const eta =
                      event.estimatedArrivalTimeWithTacho ||
                      event.estimatedArrivalTime
                    const StatusIcon = event.isCompleted
                      ? CheckCircle2
                      : event.isCurrentEvent
                        ? MapPin
                        : Circle
                    const iconToneClass =
                      status.tone === "danger"
                        ? "text-rose-500"
                        : status.tone === "success"
                          ? "text-emerald-500"
                          : status.tone === "warning"
                            ? "text-amber-500"
                            : status.tone === "info"
                              ? "text-sky-500"
                              : "text-slate-400"

                    return (
                      <li
                        key={`${event.chainId}-${event.eventId}`}
                        className="relative"
                      >
                        <span
                          className={`absolute -left-[30px] top-3 flex h-6 w-6 items-center justify-center rounded-full border-2 border-background bg-background shadow-sm ${iconToneClass}`}
                        >
                          <StatusIcon className="h-4 w-4" />
                        </span>

                        <div className="rounded-xl border bg-card px-4 py-3 shadow-sm">
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-1.5 text-xs">
                                <span className="rounded-md border border-border bg-muted px-1.5 py-0.5 font-medium text-muted-foreground">
                                  {event.kindLabel}
                                </span>
                                {event.countryCode && (
                                  <span
                                    className="text-sm leading-none"
                                    aria-hidden
                                  >
                                    {countryCodeToFlagEmoji(
                                      event.countryCode.toUpperCase(),
                                    )}
                                  </span>
                                )}
                                {event.referenceNumber && (
                                  <span className="text-muted-foreground">
                                    {event.referenceNumber}
                                  </span>
                                )}
                              </div>
                              <p className="mt-2 truncate text-sm font-semibold text-foreground">
                                {event.title}
                              </p>
                              {event.subtitle && (
                                <p className="truncate text-xs text-muted-foreground">
                                  {event.subtitle}
                                </p>
                              )}
                            </div>

                            <span
                              className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${eventStatusBadgeClasses[status.tone]}`}
                            >
                              {status.label}
                            </span>
                          </div>

                          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                            <span className="inline-flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {t("planner.liveTimeline.scheduled")}{" "}
                              <span className="font-medium text-foreground">
                                {formatTimelineTimestamp(event.scheduledTime)}
                              </span>
                            </span>
                            {eta && (
                              <span className="inline-flex items-center gap-1">
                                {t("planner.liveTimeline.eta")}{" "}
                                <span className="font-medium text-foreground">
                                  {formatTimelineTimestamp(eta)}
                                </span>
                              </span>
                            )}
                            {event.isOnRoute && (
                              <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                                <MapPin className="h-3 w-3" />
                                {t("planner.liveTimeline.onRoute")}
                                {typeof event.sequence === "number"
                                  ? ` · #${event.sequence.toString()}`
                                  : ""}
                              </span>
                            )}
                          </div>
                        </div>
                      </li>
                    )
                  })}
                </ol>
              </div>
            </>
          )}

          {selectedItem?.type === "segment" && (
            <>
              <DialogHeader className="min-w-0 pr-8">
                <DialogTitle className="break-words">
                  {selectedItem.value.segment.label}
                </DialogTitle>
                <DialogDescription>
                  {selectedItem.value.carCode +
                    " • " +
                    (selectedItem.value.driverName ||
                      t("planner.liveTimeline.unknownDriver"))}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-xl border border-border bg-muted px-4 py-3">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {t("planner.liveTimeline.route")}
                    </p>
                    <p className="mt-1 text-sm font-medium text-foreground">
                      {countryCodeToFlagEmoji(
                        selectedItem.value.segment.startCode,
                      )}{" "}
                      {selectedItem.value.segment.startCode} →{" "}
                      {countryCodeToFlagEmoji(
                        selectedItem.value.segment.endCode,
                      )}{" "}
                      {selectedItem.value.segment.endCode}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {t("planner.liveTimeline.spanDayColumns", {
                        count: selectedItem.value.segment.span,
                      })}
                    </p>
                  </div>

                  <div className="rounded-xl border border-border bg-muted px-4 py-3">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {t("planner.liveTimeline.statusLabel")}
                    </p>
                    <p className="mt-1 text-sm font-medium capitalize text-foreground">
                      {t(TONE_LABEL_KEYS[selectedItem.value.segment.tone])}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {t("planner.liveTimeline.segmentDetails")}
                    </p>
                  </div>
                </div>

                <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
                  <p className="text-sm text-foreground">
                    {t("planner.liveTimeline.segmentDescription")}
                  </p>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </ResizablePanelGroup>
  )
}

export default TimelineView
